// Phase 4 (api-phase4.md): the agent as co-pilot, the parts that work on every screen. Mounted once in the shell
// (with features.agent), next to the live layer:
//   - pending approvals: GET /api/approvals/mine on load and after every reconnect, SSE `approval` pushes one,
//     dropped when decided (here or elsewhere), expired (20 s) or cancelled. POST /api/approvals/:id answers.
//   - approval cards (prototype agentAsk toast + .approval): summary and a countdown, Approve / Deny. They float for
//     events that are not open in the console; the open event shows them in its Agent tab (with a count on the
//     tab), where Y / N answer the newest one.
//   - `watch_hit` chimes. No pop-ups for agent replies or watch hits (the user, 2026-10-28: pop-ups only for actions
//     that didn't go through); both are in the event's Agent tab.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError, errorText, type Approval, type ApprovalMessage, type EventSummary } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { askWords, reasonWords } from '../console/ai.ts';
import { useNoticeSlot, useToaster } from '../console/Toasts.tsx';
import { dur, ms } from '../console/util.ts';
import { useNow } from '../hooks.ts';
import { href, navigate } from '../router.ts';
import { useSession } from '../session.tsx';
import { useStreamEvent, useStreamStatus } from '../stream.tsx';
import { chime } from './sound.ts';

export interface PendingApproval {
  id: string;
  eventId: string;
  tool: string;
  summary: string;
  reason: string | null;
  expiresAt: string;
  requestedAt: string | null;
  /** Phase 5: the tool call's arguments (approval evidence.args), to say which speaker or relay. */
  args?: Record<string, unknown> | null;
}

/** The arguments an approval carries in its evidence (`{ args }`). */
export function approvalArgs(evidence: unknown): Record<string, unknown> | null {
  if (typeof evidence !== 'object' || evidence === null || Array.isArray(evidence)) return null;
  const a = (evidence as Record<string, unknown>).args;
  return typeof a === 'object' && a !== null && !Array.isArray(a) ? (a as Record<string, unknown>) : null;
}

export interface EventLabel {
  siteName: string;
  displayNo: string;
}

interface CopilotValue {
  /** Pending and not yet expired, oldest first. */
  approvals: PendingApproval[];
  decide: (a: PendingApproval, decision: 'approve' | 'deny') => Promise<boolean>;
  /** The approval being answered. */
  busyId: string | null;
  /** The event the console has open (null elsewhere). */
  openEventId: string | null;
  setOpenEvent: (id: string | null) => void;
  /** Refetch GET /api/approvals/mine. */
  refresh: () => void;
  label: (eventId: string) => EventLabel | null;
  /** The console shares what it knows so cards and toasts can name the site. */
  learn: (events: EventSummary[]) => void;
}

const CopilotContext = createContext<CopilotValue | null>(null);

/** The co-pilot state, or null when features.agent is off. */
export function useCopilot(): CopilotValue | null {
  return useContext(CopilotContext);
}

function fromApproval(a: Approval): PendingApproval {
  return { id: a.id, eventId: a.eventId, tool: a.tool, summary: a.summary, reason: a.reason, expiresAt: a.expiresAt, requestedAt: a.requestedAt, args: approvalArgs(a.evidence) };
}

function fromMessage(m: ApprovalMessage): PendingApproval {
  return { id: m.approvalId, eventId: m.eventId, tool: m.tool, summary: m.summary, reason: null, expiresAt: m.expiresAt, requestedAt: new Date().toISOString() };
}

/** Grace after expiresAt before a card goes (the server's expiry timer settles it). */
const EXPIRY_GRACE_MS = 1500;

export function CopilotProvider({ children }: { children: ReactNode }) {
  const s = useSession();
  const toast = useToaster();
  const stream = useStreamStatus();
  const isSup = s.has('supervisor', 'admin');
  const [list, setList] = useState<PendingApproval[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openEventId, setOpenEventId] = useState<string | null>(null);
  const [labels, setLabels] = useState<Record<string, EventLabel>>({});
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  const asked = useRef(new Set<string>());

  const refresh = useCallback(() => {
    api.approvalsMine().then(
      (l) => setList(l.filter((a) => a.status === 'pending').map(fromApproval)),
      (e: unknown) => {
        if (!(e instanceof ApiError && (e.status === 404 || e.status === 405))) console.warn('GET /api/approvals/mine failed', e);
      },
    );
  }, []);

  // On load and after every reconnect (an approval may have arrived while the stream was down).
  const wasOpen = useRef(false);
  useEffect(() => {
    const open = stream.state === 'open';
    if (open && !wasOpen.current) refresh();
    wasOpen.current = open;
  }, [stream.state, refresh]);

  const learn = useCallback((events: EventSummary[]) => {
    setLabels((m) => {
      let changed = false;
      const n = { ...m };
      for (const e of events) {
        if (n[e.id]?.displayNo === e.displayNo && n[e.id]?.siteName === e.siteName) continue;
        n[e.id] = { siteName: e.siteName, displayNo: e.displayNo };
        changed = true;
      }
      return changed ? n : m;
    });
  }, []);

  // Name the site on cards for events the console has not told us about (SSE `approval` has no site).
  const lookup = useCallback(
    (eventId: string) => {
      if (labelsRef.current[eventId] || asked.current.has(eventId)) return;
      asked.current.add(eventId);
      void (async () => {
        try {
          const mine = await api.myEvents();
          learn(mine);
          if (!mine.some((e) => e.id === eventId) && isSup) learn(await api.events({ state: 'open', limit: 200 }));
        } catch {
          /* the card shows without a site name */
        }
      })();
    },
    [isSup, learn],
  );
  useEffect(() => {
    for (const a of list) lookup(a.eventId);
  }, [list, lookup]);

  useStreamEvent('approval', (m) => {
    if (!m?.approvalId) return;
    setList((l) => (l.some((x) => x.id === m.approvalId) ? l : [...l, fromMessage(m)]));
    chime('ask');
  });
  // A decision elsewhere (another tab, a supervisor, expiry, pause) changes the event: refetch when it is one of ours.
  const listRef = useRef(list);
  listRef.current = list;
  const soon = useRef<ReturnType<typeof setTimeout> | null>(null);
  useStreamEvent('event', (e) => {
    if (!listRef.current.some((a) => a.eventId === e.id)) return;
    if (soon.current) clearTimeout(soon.current);
    soon.current = setTimeout(refresh, 300);
  });
  useEffect(() => () => void (soon.current && clearTimeout(soon.current)), []);

  // Expired cards go at expiresAt (+ grace), then the server is asked.
  const now = useNow(1000);
  const live = useMemo(() => list.filter((a) => (ms(a.expiresAt) ?? 0) + EXPIRY_GRACE_MS > now), [list, now]);
  useEffect(() => {
    if (live.length !== list.length) {
      setList(live);
      refresh();
    }
  }, [live, list.length, refresh]);

  const decide = useCallback(
    async (a: PendingApproval, decision: 'approve' | 'deny') => {
      setBusyId(a.id);
      try {
        await api.decideApproval(a.id, decision);
        setList((l) => l.filter((x) => x.id !== a.id));
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          setList((l) => l.filter((x) => x.id !== a.id));
          toast({ tone: 'warn', title: 'Too late to answer', body: `${a.summary}: ${e.message}.` });
        } else toast({ tone: 'bad', title: 'Could not answer the agent', body: errorText(e) });
        refresh();
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [toast, refresh],
  );

  const value = useMemo<CopilotValue>(
    () => ({
      approvals: live,
      decide,
      busyId,
      openEventId,
      setOpenEvent: setOpenEventId,
      refresh,
      label: (id) => labels[id] ?? null,
      learn,
    }),
    [live, decide, busyId, openEventId, refresh, labels, learn],
  );
  return (
    <CopilotContext.Provider value={value}>
      {children}
      <CopilotLayer />
    </CopilotContext.Provider>
  );
}

/* ---------- approval card ---------- */

/** 20 s countdown bar and seconds (prototype "Agent asks · expires in"). */
function useLeft(expiresAt: string) {
  const now = useNow(250);
  const exp = ms(expiresAt) ?? now;
  const shown = useRef(now);
  const total = Math.max(1000, exp - Math.min(shown.current, exp - 1000));
  const left = Math.max(0, exp - now);
  return { left, frac: Math.min(1, left / total) };
}

/**
 * One agent request (prototype .approval): what it wants to do, why, a countdown, Approve / Deny. `keys` shows the
 * Y / N hints (only on the open event's newest request).
 */
export function ApprovalCard({
  a,
  keys,
  title,
  onOpen,
  text,
}: {
  a: PendingApproval;
  keys?: boolean;
  title?: string;
  onOpen?: () => void;
  /** Phase 5: the request in words ("AI asks to play 'Leave the property' on Fence East"); else the summary. */
  text?: string | null;
}) {
  const cop = useCopilot();
  const { left, frac } = useLeft(a.expiresAt);
  const busy = cop?.busyId === a.id;
  const go = (d: 'approve' | 'deny') => void cop?.decide(a, d);
  const secs = Math.ceil(left / 1000);
  const what = text ?? askWords(a.summary, a.tool);
  return (
    <div className="approval" role="group" aria-label="The agent asks for approval">
      <span className="lbl">
        <Icon name="alert" />
        {title ?? 'Agent asks'} · expires in <span className={`mono${left < 5000 ? ' late' : ''}`}>{left > 0 ? `0:${String(secs).padStart(2, '0')}` : dur(0)}</span>
      </span>
      <div className="abar" aria-hidden="true">
        <i style={{ transform: `scaleX(${frac})` }} />
      </div>
      <b>{what}</b>
      {a.reason && <div className="note">Why it asks: {reasonWords(a.reason, a.tool) ?? a.reason}.</div>}
      <div className="row-wrap">
        <button type="button" className="btn pri" onClick={() => go('approve')} disabled={busy || left <= 0}>
          <Icon name="check" />
          Approve {keys && <kbd>Y</kbd>}
        </button>
        <button type="button" className="btn" onClick={() => go('deny')} disabled={busy || left <= 0}>
          Deny {keys && <kbd>N</kbd>}
        </button>
        {onOpen && (
          <>
            <span className="sp" />
            <button type="button" className="btn ghost" onClick={onOpen}>
              Open event
            </button>
          </>
        )}
      </div>
      <div className="note">No answer in time counts as a denial.</div>
    </div>
  );
}

/* ---------- the layer: floating cards and toasts ---------- */

function CopilotLayer() {
  const cop = useCopilot();
  const toast = useToaster();
  const slot = useNoticeSlot();
  const copRef = useRef(cop);
  copRef.current = cop;

  // An AI watch hit chimes; what it saw is in the event's Agent tab (no pop-up, 2026-10-28).
  useStreamEvent('watch_hit', (m) => {
    if (m?.eventId) chime('raise');
  });

  if (!cop || !slot) return null;
  const floating = cop.approvals.filter((a) => a.eventId !== cop.openEventId);
  if (!floating.length) return null;
  // In the notices column above the toasts (Toasts.tsx), so the two never overlap.
  return createPortal(
    <div className="askstack" aria-live="polite">
      {floating.slice(-3).map((a) => {
        const l = cop.label(a.eventId);
        return <ApprovalCard key={a.id} a={a} title={l ? `Agent asks · ${l.siteName} · ${l.displayNo}` : 'Agent asks'} onOpen={() => navigate(href.console(a.eventId))} />;
      })}
    </div>,
    slot,
  );
}
